/**
 * EE Results timeline — GSAP ScrollTrigger (scoped per [data-ee-timeline]).
 * Desktop: in-flow scrub (no pin / no fixed / no layout reparent); opacity fades sped up vs prior.
 * Loads GSAP + ScrollTrigger from CDN once; respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const GSAP_CORE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  const SCROLL_TRIGGER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

  function loadScript(scriptUrl) {
    return new Promise(function (resolve, reject) {
      const scriptElement = document.createElement('script');
      scriptElement.src = scriptUrl;
      scriptElement.async = false;
      scriptElement.onload = resolve;
      scriptElement.onerror = reject;
      document.head.appendChild(scriptElement);
    });
  }

  function ensureGsapLoaded() {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();
    if (window.__eeResultsTimelineGsapPromise) return window.__eeResultsTimelineGsapPromise;
    window.__eeResultsTimelineGsapPromise = (async function () {
      if (!window.gsap) await loadScript(GSAP_CORE_URL);
      if (!window.ScrollTrigger) await loadScript(SCROLL_TRIGGER_URL);
    })();
    return window.__eeResultsTimelineGsapPromise;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isShopifyThemeEditor(timelineRoot) {
    return (
      (timelineRoot && timelineRoot.getAttribute('data-ee-timeline-editor') === 'true') ||
      (typeof window.Shopify !== 'undefined' && window.Shopify.designMode)
    );
  }

  /**
   * ScrollTrigger must use the same element that actually scrolls. If a layout wrapper uses
   * overflow-y: auto|scroll, window scroll and ST progress disagree (“layout looks right but
   * triggers don’t match”). Fall back to document.scrollingElement for normal window scroll.
   *
   * Threshold for “has meaningful scroll” is intentionally small: some themes only expose a
   * few px of overflow on `#MainContent` / inner shells while still being the real scroller.
   */
  function getScrollContainerElement(startFromElement) {
    let ancestor = startFromElement;
    while (ancestor && ancestor !== document.documentElement) {
      const computedStyle = window.getComputedStyle(ancestor);
      const overflowY = computedStyle.overflowY;
      const overflowX = computedStyle.overflowX;
      const isScrollableOverflowY =
        overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
      const isScrollableOverflowX =
        overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay';
      const maxScrollableDistanceY = ancestor.scrollHeight - ancestor.clientHeight;
      const maxScrollableDistanceX = ancestor.scrollWidth - ancestor.clientWidth;
      if (
        (isScrollableOverflowY && maxScrollableDistanceY > 8) ||
        (isScrollableOverflowX && maxScrollableDistanceX > 8)
      ) {
        return ancestor;
      }
      ancestor = ancestor.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  /**
   * Scrub math uses the scroller’s viewport. If ScrollTrigger progress feels wrong vs what you
   * see scrolling, the wrong **scroller** is often the cause — ST is integrating a different
   * scroll container than the one that actually moves.
   *
   * Optional: `data-ee-tl-scroller="#MainContent"` (CSS selector) on `[data-ee-timeline]` to force
   * the scroll root when autodetection misses a subtle inner scroller.
   */
  function resolveScrollTriggerScroller(timelineRoot) {
    if (timelineRoot && timelineRoot.dataset && timelineRoot.dataset.eeTlScroller) {
      try {
        const selector = String(timelineRoot.dataset.eeTlScroller).trim();
        if (selector && typeof document.querySelector === 'function') {
          const explicitScroller = document.querySelector(selector);
          if (explicitScroller && explicitScroller.nodeType === 1) return explicitScroller;
        }
      } catch (e) {
        /* ignore invalid selector */
      }
    }

    const candidate = getScrollContainerElement(timelineRoot);
    const scrollingElement = document.scrollingElement;
    const documentElement = document.documentElement;
    if (!candidate || candidate === documentElement || (scrollingElement && candidate === scrollingElement)) {
      return window;
    }
    return candidate;
  }

  /**
   * Scroll distance (px) after `start` over which the desktop scrub timeline runs.
   * No pin: the section stays in normal flow; scroll progress maps to timeline 0→1.
   */
  function getDesktopScrubDistancePx(timelineRoot, phaseCount) {
    const root = timelineRoot;
    const h = root && root.offsetHeight ? root.offsetHeight : 640;
    const multiplier = phaseCount <= 1 ? 1.05 : phaseCount === 2 ? 1.15 : 1.25;
    return Math.min(Math.max(Math.round(h * multiplier), 420), 2400);
  }

  /**
   * Theme editor: static mid-scroll preview (first pill lit, partial fill) — not full end state.
   */
  function applyEditorPreviewState(timelineRoot) {
    timelineRoot.classList.remove('ee-results-timeline--prep');
    timelineRoot.classList.add('ee-results-timeline--editor-preview');
    timelineRoot.querySelectorAll('.ee-tl__dot').forEach(function (dotElement, index) {
      dotElement.classList.toggle('is-lit', index === 0);
    });
    const trackFill = timelineRoot.querySelector('[data-ee-tl-fill]');
    if (trackFill) {
      trackFill.style.width = '';
      trackFill.style.transform = 'scaleX(0.35)';
    }
    timelineRoot
      .querySelectorAll('.ee-tl__title, .ee-tl__track, .ee-tl__phase-name, .ee-tl__dot')
      .forEach(function (element) {
        element.style.opacity = '1';
        element.style.transform = '';
      });
    timelineRoot.querySelectorAll('[data-ee-tl-content]').forEach(function (element) {
      element.style.opacity = '1';
      element.style.transform = '';
    });
    const divider = timelineRoot.querySelector('[data-ee-tl-divider]');
    const callToAction = timelineRoot.querySelector('[data-ee-tl-cta]');
    if (divider) divider.style.opacity = '1';
    if (callToAction) {
      callToAction.style.opacity = '1';
      callToAction.style.transform = '';
    }
  }

  function applyFinalVisibleState(timelineRoot) {
    timelineRoot.classList.remove('ee-results-timeline--prep');
    timelineRoot.classList.remove('ee-results-timeline--pin-scrub-complete');
    timelineRoot.classList.remove('ee-results-timeline--editor-preview');
    timelineRoot.querySelectorAll('.ee-tl__dot').forEach(function (dotElement) {
      dotElement.classList.add('is-lit');
    });
    const trackFill = timelineRoot.querySelector('[data-ee-tl-fill]');
    if (trackFill) {
      trackFill.style.width = '100%';
      trackFill.style.transform = 'scaleX(1)';
    }
    timelineRoot
      .querySelectorAll('.ee-tl__title, .ee-tl__track, .ee-tl__phase-name, .ee-tl__dot')
      .forEach(function (element) {
        element.style.opacity = '1';
        element.style.transform = 'none';
      });
    timelineRoot.querySelectorAll('[data-ee-tl-content]').forEach(function (element) {
      element.style.opacity = '1';
      element.style.transform = 'none';
    });
    const divider = timelineRoot.querySelector('[data-ee-tl-divider]');
    const callToAction = timelineRoot.querySelector('[data-ee-tl-cta]');
    if (divider) divider.style.opacity = '1';
    if (callToAction) {
      callToAction.style.opacity = '1';
      callToAction.style.transform = 'none';
    }
  }

  function destroyTimeline(timelineRoot) {
    if (timelineRoot.__eeTimelineStRefresh) {
      clearTimeout(timelineRoot.__eeTimelineStRefresh);
      timelineRoot.__eeTimelineStRefresh = null;
    }
    if (timelineRoot.__eeTimelineGsapContext) {
      timelineRoot.__eeTimelineGsapContext.revert();
      timelineRoot.__eeTimelineGsapContext = null;
    }
    timelineRoot.dataset.eeTimelineLoading = 'false';
    applyFinalVisibleState(timelineRoot);
  }

  function collectDotElements(timelineRoot) {
    const dotElements = [];
    let dotIndex = 1;
    while (true) {
      const dot = timelineRoot.querySelector('[data-ee-tl-dot="' + dotIndex + '"]');
      if (!dot) break;
      dotElements.push(dot);
      dotIndex += 1;
    }
    return dotElements;
  }

  /** Progress thresholds (0–1) at which each dot should appear “lit” on desktop scrub. */
  function getDesktopDotLitProgressThresholds(phaseCount) {
    if (phaseCount === 1) return [0.12];
    if (phaseCount === 2) return [0.12, 0.48];
    return [0.12, 0.46, 0.78];
  }

  function isDesktopDotLitAtProgress(dotIndexOneBased, phaseCount, timelineProgress) {
    const thresholds = getDesktopDotLitProgressThresholds(phaseCount);
    const thresholdForDot = thresholds[dotIndexOneBased - 1];
    return typeof thresholdForDot === 'number' && timelineProgress >= thresholdForDot;
  }

  function createDesktopScrollTimeline(timelineRoot, gsap, dotElements, trackFillElement, phaseCount) {
    const easeOutPower2 = 'power2.out';
    const divider = timelineRoot.querySelector('[data-ee-tl-divider]');
    const callToAction = timelineRoot.querySelector('[data-ee-tl-cta]');
    const titleElements = timelineRoot.querySelectorAll('.ee-tl__title');
    const phaseNameElements = timelineRoot.querySelectorAll('.ee-tl__phase-name');
    const dotElementsAll = timelineRoot.querySelectorAll('.ee-tl__dot');
    const desktopScroller = resolveScrollTriggerScroller(timelineRoot);

    const scrollTriggerConfig = {
      trigger: timelineRoot,
      pin: false,
      scroller: desktopScroller,
      start: phaseCount === 0 ? 'top bottom' : 'top 88%',
      end: function () {
        if (phaseCount === 0) return '+=80%';
        return '+=' + getDesktopScrubDistancePx(timelineRoot, phaseCount);
      },
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate: (function () {
        let lastDotLitSignature = -1;
        return function (scrollTriggerInstance) {
          if (phaseCount === 0) return;
          const timelineProgress =
            typeof scrollTriggerInstance.progress === 'number'
              ? scrollTriggerInstance.progress
              : 0;
          let signature = 0;
          for (let index = 0; index < dotElements.length; index += 1) {
            if (isDesktopDotLitAtProgress(index + 1, phaseCount, timelineProgress)) {
              signature |= 1 << index;
            }
          }
          if (signature === lastDotLitSignature) return;
          lastDotLitSignature = signature;
          dotElements.forEach(function (dotElement, index) {
            dotElement.classList.toggle('is-lit', (signature & (1 << index)) !== 0);
          });
        };
      })(),
    };

    const timeline = gsap.timeline({
      scrollTrigger: scrollTriggerConfig,
    });

    if (phaseCount === 0) {
      if (titleElements.length) {
        timeline.to(titleElements, { opacity: 1, ease: easeOutPower2, duration: 0.06 }, 0);
      }
      /* Rail (.ee-tl__track): do not tween opacity — prep used opacity:0 and GSAP would leave
       * inline opacity:0 after prep is removed, hiding the bar until scrub advanced. */
      if (phaseNameElements.length) {
        timeline.to(
          phaseNameElements,
          { opacity: 1, ease: easeOutPower2, duration: 0.05, stagger: 0.01 },
          0.02
        );
      }
      if (dotElementsAll.length) {
        timeline.to(
          dotElementsAll,
          { opacity: 1, ease: easeOutPower2, duration: 0.05, stagger: 0.01 },
          0.02
        );
      }
      if (divider) timeline.to(divider, { opacity: 1, ease: easeOutPower2, duration: 0.06 }, 0.38);
      if (callToAction) {
        timeline.to(callToAction, { opacity: 1, ease: easeOutPower2, duration: 0.09 }, 0.4);
      }
      return;
    }

    if (titleElements.length) {
      timeline.to(titleElements, { opacity: 1, ease: easeOutPower2, duration: 0.05 }, 0);
    }
    if (phaseNameElements.length) {
      timeline.to(
        phaseNameElements,
        { opacity: 1, ease: easeOutPower2, duration: 0.04, stagger: 0.01 },
        0.05
      );
    }
    if (dotElementsAll.length) {
      timeline.to(
        dotElementsAll,
        { opacity: 1, ease: easeOutPower2, duration: 0.04, stagger: 0.01 },
        0.05
      );
    }
    if (trackFillElement) {
      timeline.to(trackFillElement, { scaleX: 0, duration: 0.01, force3D: true }, 0.06);
    }

    if (phaseCount === 1) {
      const phaseOneContent = timelineRoot.querySelector('[data-ee-tl-content="1"]');
      if (phaseOneContent) {
        timeline.to(phaseOneContent, { opacity: 1, ease: easeOutPower2, duration: 0.08 }, 0.07);
      }
      if (trackFillElement) {
        timeline.to(trackFillElement, { scaleX: 1, ease: 'none', duration: 0.11, force3D: true }, 0.15);
      }
      if (divider) timeline.to(divider, { opacity: 1, duration: 0.05 }, 0.36);
      if (callToAction) {
        timeline.to(callToAction, { opacity: 1, ease: easeOutPower2, duration: 0.06 }, 0.38);
      }
      return;
    }

    if (phaseCount === 2) {
      const phaseOneBody = timelineRoot.querySelector('[data-ee-tl-content="1"]');
      const phaseTwoBody = timelineRoot.querySelector('[data-ee-tl-content="2"]');
      if (phaseOneBody) {
        timeline.to(phaseOneBody, { opacity: 1, ease: easeOutPower2, duration: 0.07 }, 0.07);
      }
      if (trackFillElement) {
        timeline.to(trackFillElement, { scaleX: 1, ease: 'none', duration: 0.12, force3D: true }, 0.14);
      }
      if (phaseTwoBody) {
        timeline.to(phaseTwoBody, { opacity: 1, ease: easeOutPower2, duration: 0.07 }, 0.24);
      }
      if (divider) timeline.to(divider, { opacity: 1, duration: 0.04 }, 0.36);
      if (callToAction) {
        timeline.to(callToAction, { opacity: 1, ease: easeOutPower2, duration: 0.06 }, 0.38);
      }
      return;
    }

    const phaseOneContent = timelineRoot.querySelector('[data-ee-tl-content="1"]');
    const phaseTwoContent = timelineRoot.querySelector('[data-ee-tl-content="2"]');
    const phaseThreeContent = timelineRoot.querySelector('[data-ee-tl-content="3"]');
    if (phaseOneContent) {
      timeline.to(phaseOneContent, { opacity: 1, ease: easeOutPower2, duration: 0.07 }, 0.07);
    }
    if (trackFillElement) {
      timeline.to(trackFillElement, { scaleX: 0.5, ease: 'none', duration: 0.1, force3D: true }, 0.13);
    }
    if (phaseTwoContent) {
      timeline.to(phaseTwoContent, { opacity: 1, ease: easeOutPower2, duration: 0.07 }, 0.24);
    }
    if (trackFillElement) {
      timeline.to(trackFillElement, { scaleX: 1, ease: 'none', duration: 0.09, force3D: true }, 0.3);
    }
    if (phaseThreeContent) {
      timeline.to(phaseThreeContent, { opacity: 1, ease: easeOutPower2, duration: 0.07 }, 0.4);
    }
    if (divider) timeline.to(divider, { opacity: 1, duration: 0.04 }, 0.44);
    if (callToAction) {
      timeline.to(callToAction, { opacity: 1, ease: easeOutPower2, duration: 0.06 }, 0.46);
    }
  }

  function createGsapContext(timelineRoot, gsap) {
    const dotElements = collectDotElements(timelineRoot);
    const trackFillElement = timelineRoot.querySelector('[data-ee-tl-fill]');
    const phaseCount = dotElements.length;

    return gsap.context(function () {
      const matchMedia = gsap.matchMedia();

      matchMedia.add('(min-width: 768px)', function () {
        createDesktopScrollTimeline(
          timelineRoot,
          gsap,
          dotElements,
          trackFillElement,
          phaseCount
        );
      });

      matchMedia.add('(max-width: 767px)', function () {
        const headerElement = timelineRoot.querySelector('.ee-tl__header');
        gsap.to(timelineRoot.querySelectorAll('.ee-tl__title'), {
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          duration: 0.6,
          scrollTrigger: { trigger: headerElement || timelineRoot, start: 'top 85%' },
        });

        const phaseElements = timelineRoot.querySelectorAll('.ee-tl__phase');
        const firstPhaseElement = phaseElements[0];
        const firstDotElement = timelineRoot.querySelector('[data-ee-tl-dot="1"]');

        const ScrollTriggerPlugin = window.ScrollTrigger;
        if (ScrollTriggerPlugin && firstPhaseElement && firstDotElement) {
          ScrollTriggerPlugin.create({
            trigger: firstPhaseElement,
            start: 'top 85%',
            end: 'top 68%',
            scrub: true,
            onUpdate: function (scrollTriggerInstance) {
              firstDotElement.classList.toggle('is-lit', scrollTriggerInstance.progress > 0.35);
            },
          });
        }

        timelineRoot.querySelectorAll('.ee-tl__phase').forEach(function (phaseElement, index) {
          const phaseIndexOneBased = index + 1;
          const dotForPhase = timelineRoot.querySelector(
            '[data-ee-tl-dot="' + phaseIndexOneBased + '"]'
          );
          const bodyForPhase = timelineRoot.querySelector(
            '[data-ee-tl-content="' + phaseIndexOneBased + '"]'
          );
          const phaseTitleElement = phaseElement.querySelector('.ee-tl__phase-name');
          const phaseScrollTrigger = { trigger: phaseElement, start: 'top 80%' };

          if (phaseTitleElement) {
            gsap.to(phaseTitleElement, {
              opacity: 1,
              y: 0,
              ease: 'power2.out',
              duration: 0.45,
              scrollTrigger: phaseScrollTrigger,
            });
          }

          if (dotForPhase) {
            gsap.to(dotForPhase, {
              opacity: 1,
              ease: 'power2.out',
              duration: 0.45,
              delay: 0.08,
              scrollTrigger: phaseScrollTrigger,
            });
          }

          if (bodyForPhase) {
            gsap.to(bodyForPhase, {
              opacity: 1,
              y: 0,
              ease: 'power2.out',
              duration: 0.55,
              delay: 0.15,
              scrollTrigger: phaseScrollTrigger,
            });
          }
        });

        timelineRoot.querySelectorAll('[data-ee-tl-conn]').forEach(function (connectorElement) {
          const connectorIndex = parseInt(connectorElement.getAttribute('data-ee-tl-conn'), 10);
          if (Number.isNaN(connectorIndex)) return;
          const connectorFill = connectorElement.querySelector('.ee-tl__connector-fill');
          const nextDotAfterConnector = timelineRoot.querySelector(
            '[data-ee-tl-dot="' + (connectorIndex + 1) + '"]'
          );
          if (!connectorFill) return;

          gsap.fromTo(
            connectorFill,
            { scaleY: 0 },
            {
              scaleY: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: connectorElement,
                start: 'top 88%',
                end: 'bottom 52%',
                scrub: true,
                onUpdate: function (scrollTriggerInstance) {
                  if (nextDotAfterConnector && scrollTriggerInstance.progress > 0.55) {
                    nextDotAfterConnector.classList.add('is-lit');
                  }
                },
              },
            }
          );
        });

        const mobileCallToAction = timelineRoot.querySelector('[data-ee-tl-cta]');
        if (mobileCallToAction) {
          gsap.to(mobileCallToAction, {
            opacity: 1,
            y: 0,
            ease: 'power2.out',
            duration: 0.5,
            scrollTrigger: { trigger: mobileCallToAction, start: 'top 90%' },
          });
        }
      });
    }, timelineRoot);
  }

  function initializeTimelineRoot(timelineRoot) {
    if (timelineRoot.dataset.eeTimelineReady === 'true') return;
    if (timelineRoot.dataset.eeTimelineLoading === 'true') return;

    if (prefersReducedMotion()) {
      applyFinalVisibleState(timelineRoot);
      timelineRoot.dataset.eeTimelineReady = 'true';
      return;
    }

    if (isShopifyThemeEditor(timelineRoot)) {
      applyEditorPreviewState(timelineRoot);
      timelineRoot.dataset.eeTimelineReady = 'true';
      return;
    }

    timelineRoot.dataset.eeTimelineLoading = 'true';
    timelineRoot.classList.add('ee-results-timeline--prep');

    ensureGsapLoaded()
      .then(function () {
        const gsap = window.gsap;
        gsap.registerPlugin(window.ScrollTrigger);
        timelineRoot.__eeTimelineGsapContext = createGsapContext(timelineRoot, gsap);
        timelineRoot.classList.remove('ee-results-timeline--prep');
        timelineRoot.dataset.eeTimelineReady = 'true';
        timelineRoot.dataset.eeTimelineLoading = 'false';
        if (window.ScrollTrigger) {
          requestAnimationFrame(function () {
            window.ScrollTrigger.refresh();
          });
          timelineRoot.__eeTimelineStRefresh = setTimeout(function () {
            timelineRoot.__eeTimelineStRefresh = null;
            window.ScrollTrigger.refresh();
          }, 320);
        }
      })
      .catch(function () {
        timelineRoot.classList.remove('ee-results-timeline--prep');
        timelineRoot.dataset.eeTimelineLoading = 'false';
      });
  }

  function bootAllTimelineSections() {
    document.querySelectorAll('[data-ee-timeline]').forEach(initializeTimelineRoot);
  }

  document.addEventListener('shopify:section:unload', function (event) {
    const sectionElement = event.target;
    if (!sectionElement || !sectionElement.querySelector) return;
    const timelineRoot = sectionElement.querySelector('[data-ee-timeline]');
    if (!timelineRoot) return;
    destroyTimeline(timelineRoot);
    timelineRoot.dataset.eeTimelineReady = 'false';
  });

  document.addEventListener('shopify:section:load', function (event) {
    const sectionElement = event.target;
    if (!sectionElement || !sectionElement.querySelector) return;
    const timelineRoot = sectionElement.querySelector('[data-ee-timeline]');
    if (!timelineRoot) return;
    timelineRoot.dataset.eeTimelineReady = 'false';
    initializeTimelineRoot(timelineRoot);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAllTimelineSections);
  } else {
    bootAllTimelineSections();
  }
})();
